// Live calls and call tracing for one line, at full width.
//
// Kevin, 2026-08-22: "need to be able to view live calls and call tracing from the line detail
// page." Both existed only in the sticky Diagnostics rail — a 320px column, which is enough for a
// count and a button and nowhere near enough for a SIP message or a channel dump. This is the same
// data with room to read it.
//
// WHY IT DUPLICATES THE RAIL RATHER THAN REPLACING IT. The rail answers "are the phones up" on
// whatever tab you are on, and that stays. This answers "what is happening on this call, right
// now", which is a thing you go looking for.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import SipMessageTable, { CopyButton, traceAsText } from './SipMessageTable.jsx';
import SupportModal from '../shared/SupportModal.jsx';

// 900 IS THE REAL CEILING, and the reason we thought otherwise was ours.
//
// The picker was cut to 2 and 5 minutes after a request for 900 came back counting down from 300.
// Cause (AGENT_ASKS #35): we sent the duration as `seconds`, the agent's field is `ttlSecs`, the
// unrecognised field was dropped and the 300s default applied — so the ceiling was never the
// problem. Fixed at the source; 901 is genuinely refused, so 15 minutes is offerable again.
//
// The card still displays the countdown the agent reports rather than the number asked for, which
// is what would have made the original bug obvious in one glance.
const DURATIONS = [
  { label: '2 minutes', seconds: 120 },
  { label: '5 minutes', seconds: 300 },
  { label: '15 minutes', seconds: 900 },
];

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 py-1.5 text-sm last:border-0">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right text-gray-900">{children}</dd>
    </div>
  );
}

/** One live channel, expandable into `core show channel`. */
function LiveCall({ line, chan }) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['line', String(line.id), 'channel', chan.channel],
    queryFn: () => api.get(`/lines/${line.id}/channel?channel=${encodeURIComponent(chan.channel)}`),
    enabled: open,
    retry: false,
    refetchInterval: open ? 5_000 : false,
  });

  // A channel that ended between the list and the click is the ordinary case, not an error worth
  // shouting about — the call simply finished.
  const gone = detail.error && (detail.error.status === 404 || detail.error.body?.code === 'channel_gone');

  return (
    <div className="rounded border border-gray-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
      >
        <span className="font-mono text-xs text-gray-800">{chan.channel}</span>
        <span className="flex items-center gap-3 text-xs text-gray-600">
          {chan.state && <span>{chan.state}</span>}
          {Number.isFinite(Number(chan.rtt)) && <span>rtt {chan.rtt}ms</span>}
          {Number.isFinite(Number(chan.jitter)) && <span>jitter {chan.jitter}ms</span>}
          {Number.isFinite(Number(chan.loss)) && <span>loss {chan.loss}</span>}
          <span className="text-gray-400">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-200 px-3 py-2">
          {detail.isLoading && <p className="text-xs text-gray-500">Reading the channel…</p>}
          {gone && <p className="text-xs text-gray-500">That call has ended.</p>}
          {detail.error && !gone && (
            <p className="text-xs text-red-600">{detail.error.message || 'could not read the channel'}</p>
          )}
          {detail.data && (
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-snug text-gray-700">
              {typeof detail.data.detail === 'string'
                ? detail.data.detail
                : JSON.stringify(detail.data.detail, null, 1)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export default function LineLiveCalls({ line, canWrite }) {
  const qc = useQueryClient();
  const [seconds, setSeconds] = useState(300);
  const [err, setErr] = useState(null);
  const [ticket, setTicket] = useState(false);

  const quality = useQuery({
    queryKey: ['line', String(line.id), 'live-quality'],
    queryFn: () => api.get(`/lines/${line.id}/live-quality`),
    retry: false,
    refetchInterval: 5_000,
  });
  const traceQ = useQuery({
    queryKey: ['line', String(line.id), 'trace'],
    queryFn: () => api.get(`/lines/${line.id}/trace`),
    retry: false,
    refetchInterval: 5_000,
  });

  const trace = traceQ.data?.trace || null;
  // `active` is the agent's field. Reading `running` here showed "off" straight through a live
  // trace once already.
  const running = trace?.active === true;
  // A host we could not reach is NOT "no trace running" — saying so would let an operator believe
  // they had switched something off that is still on.
  const unknown = Boolean(traceQ.error) || trace?.unknown === true;

  const start = useMutation({
    mutationFn: () => api.post(`/lines/${line.id}/trace`, { seconds }),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['line', String(line.id), 'trace'] }); },
    onError: (e) => setErr(e?.message || 'could not start the trace'),
  });
  const stop = useMutation({
    mutationFn: () => api.delete(`/lines/${line.id}/trace`),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['line', String(line.id), 'trace'] }); },
    onError: (e) => setErr(e?.message || 'could not stop the trace'),
  });

  const channels = quality.data?.channels || [];
  const sip = quality.data?.sip_history || null;
  const others = sip?.other_extensions || [];
  // Signalling evidence of calls, which is independent of whether they ever got media.
  const attempts = Number(sip?.methods?.INVITE || 0);
  const ended = Number(sip?.methods?.BYE || 0) + Number(sip?.methods?.CANCEL || 0);

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Calls in progress</h3>
        {quality.error ? (
          // Not "no calls". We could not ask.
          <p className="mt-2 text-sm text-amber-800">
            Could not ask the host: {quality.error.message || 'unavailable'}
          </p>
        ) : channels.length === 0 ? (
          // WHAT THIS LIST ACTUALLY COVERS. Kevin, 2026-08-22: "call tracing in progress but no
          // calls in progress." It was telling the truth about the wrong thing. `pjsip show
          // channelstats` reports RTP statistics, and RTP exists only once a call has media — so a
          // RINGING call is never in it. Every test call that day rang and was cancelled before
          // answer, so this read "no calls" throughout, while the phone was audibly ringing.
          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-500">
              No call with audio on this line right now. This lists calls that have been answered —
              a ringing call has no media yet and appears in the SIP messages below, not here.
            </p>
            {attempts > 0 && (
              <p className="text-sm text-gray-700">
                {attempts} call attempt{attempts === 1 ? '' : 's'} in the trace buffer
                {ended > 0 ? `, ${ended} already ended` : ''} — see SIP messages.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {channels.map((c) => <LiveCall key={c.channel} line={line} chan={c} />)}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Call tracing</h3>
        <dl className="mt-2">
          <Row label="State">
            {unknown ? <span className="text-amber-800">unknown — could not ask the host</span>
              : running ? <span className="text-green-700">on</span>
                : <span className="text-gray-500">off</span>}
          </Row>
          {running && (
            <>
              <Row label="Stops in">{trace.secondsRemaining ?? '—'}s</Row>
              <Row label="Watching">{trace.scope || '—'}</Row>
              {trace.startedBy && <Row label="Started by">{trace.startedBy}</Row>}
            </>
          )}
        </dl>

        {canWrite && (
          <div className="mt-3 flex items-center gap-2">
            {!running && (
              <select
                value={seconds}
                onChange={(e) => setSeconds(Number(e.target.value))}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
              >
                {DURATIONS.map((d) => (
                  <option key={d.seconds} value={d.seconds}>{d.label}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              disabled={start.isPending || stop.isPending}
              onClick={() => (running ? stop.mutate() : start.mutate())}
              className={`rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                running ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {running ? 'Stop tracing' : 'Start tracing'}
            </button>
            <span className="text-xs text-gray-500">
              Logs this line&apos;s SIP only, and expires on its own.
            </span>
          </div>
        )}
        {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="text-sm font-semibold text-gray-900">SIP messages</h3>
          <div className="flex items-center gap-3">
            {sip && (
              <p className="text-xs text-gray-500">
                {sip.matched} of {sip.total_in_ring} in the host&apos;s buffer
              </p>
            )}
            <CopyButton
              disabled={!sip || !sip.matched}
              text={sip ? traceAsText({ line, sip, mine: quality.data?.extensions || [] }) : ''}
            />
            {/* The trace is evidence, and evidence is mostly wanted by somebody else. Copy covers
                "paste it to a colleague"; this covers "raise it with support" without the operator
                hand-assembling a paste that loses its denominator. */}
            <button
              type="button"
              disabled={!sip || !sip.matched}
              onClick={() => setTicket(true)}
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Submit a ticket
            </button>
          </div>
        </div>

        {sip?.findings?.map((f) => (
          <p
            key={f.text}
            className={`mt-1 text-sm leading-snug ${f.level === 'warn' ? 'text-amber-800' : 'text-gray-600'}`}
          >
            {f.text}
          </p>
        ))}

        {others.length > 0 && (
          // The address half of the filter matches every device behind the traced router. Said out
          // loud, because the alternative is a table that quietly includes somebody else's phone.
          <p className="mt-1 text-sm text-amber-800">
            Includes traffic from {others.length} other extension{others.length === 1 ? '' : 's'}{' '}
            behind the same address ({others.join(', ')}) — highlighted below.
          </p>
        )}

        <div className="mt-3 overflow-x-auto">
          {sip && sip.matched > 0 ? (
            <SipMessageTable rows={sip.rows || []} mine={quality.data?.extensions || []} />
          ) : (
            <p className="text-sm text-gray-500">
              {running
                ? 'Nothing for this line yet — place or receive a call while the trace runs.'
                : 'Start a trace, then place or receive a call to collect SIP messages.'}
            </p>
          )}
        </div>
      </section>

      {ticket && sip && (
        <SupportModal
          onClose={() => setTicket(false)}
          initialSubject={`Call trace — ${line.number || line.label || `line ${line.id}`}`}
          warning={
            attempts === 0
              ? (running
                ? 'No call in this capture yet. Place or receive a call while the trace runs, then submit — otherwise this attaches keep-alives only.'
                : 'No trace is running and there is no call in this capture — it is keep-alive traffic only. Start a trace, make the call, then submit.')
              : (!running
                ? 'The trace is not running; this is what remains in the host buffer, which rolls over quickly.'
                : null)
          }
          initialDescription={
            // Prefilled with the facts, not with a guess at the complaint. The operator says what
            // is wrong; the trace says what happened.
            [
              `Line ${line.number || line.id} on ${line.pbxName || 'its PBX host'}.`,
              `${sip.matched} of ${sip.total_in_ring} messages in the host's SIP buffer matched this line.`,
              ...(sip.findings || []).map((f) => `- ${f.text}`),
              '',
              'What I was doing / what went wrong: ',
            ].join('\n')
          }
          attachment={{
            kind: 'sip_trace',
            // WHAT THIS CAPTURE ACTUALLY IS. Ticket #21 was filed with 46 matched messages and no
            // call in any of them: the trace had expired a quarter of an hour earlier and what
            // remained in the ring was keep-alives. The attachment now states whether a trace was
            // running at capture and whether any call signalling is present, so triage is never
            // handed a table of OPTIONS under the name "call trace".
            trace_active: running,
            trace_seconds_remaining: running ? (trace?.secondsRemaining ?? null) : null,
            contains_call_signalling: attempts > 0,
            captured_with_trace_off: !running,
            label: `SIP trace for ${line.number || `line ${line.id}`}`,
            line_id: line.id,
            host: quality.data?.host ?? line.pbxName ?? null,
            extensions: quality.data?.extensions || [],
            matched: sip.matched,
            total_in_ring: sip.total_in_ring,
            other_extensions: sip.other_extensions || [],
            findings: sip.findings || [],
            methods: sip.methods || {},
            statuses: sip.statuses || {},
            rows: sip.rows || [],
          }}
        />
      )}
    </div>
  );
}
